import React, { useContext, useEffect } from 'react';
import FormGroup from '@mui/material/FormGroup';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import useForm from '../../../components/_hook/useForm';
import { useUpdateMinisiteMutation } from '../../../../graphql/mutation/minisite/updateMinisiteMutation';
import useNotification from '../../../components/_hook/useNotification';
import useRouter from '../../../components/_hook/useRouter';
import { useMinisiteQuery } from '../../../../graphql/query/minisite/useMinisiteQuery';
import NotFound from '../../../components/_error/NotFound';
import useRoutes from '../../../components/_hook/useRoutes';
import { useCreateMinisiteMutation } from '../../../../graphql/mutation/minisite/createMinisiteMutation';
import MinisiteLayout from '../../../layout/Minisite';
import Context from '../../../context';
import SuperAdminPermission from '../../../adminComponents/_permission/SuperAdminPermission';
import Helmet from '../../../components/Helmet';
import StickyFormFooter from '../../../components/_form/StickyFormFooter';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
  },
  sourceUrlPaper: {
    width: '100%',
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
  tab: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#000',
  },
}));

function MinisiteDetail() {
  interface IFormValue {
    status: boolean;
    redirectToHttps: boolean;
    domains: string[];
    seoKeywords: string;
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { docID: minisiteId } = params;
  const { company } = useContext(Context);

  const { data, loading } = useMinisiteQuery(
    `
			_id
            status
            redirectToHttps
            domain
            seoKeywords
		`,
    {
      variables: {
        filter: {
          companyId: company._id,
          _id: minisiteId as string,
        },
      },
      skip: minisiteId === 'new',
    }
  );

  function onCompleted() {
    notification.success(t('success'));
    history.push(
      path('minisite.list', {
        companyID: company._id,
      })
    );
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateMinisiteMutation,
    { loading: isUpdatingMinisite },
  ] = useUpdateMinisiteMutation(
    `updatedMinisite {
				_id
                status
                redirectToHttps
                domain
			}`,
    {
      onCompleted,
      onError,
    }
  );

  const [
    createMinisiteMutation,
    { loading: isCreatingMinisite },
  ] = useCreateMinisiteMutation(
    `createdMinisite {
				_id
                status
                redirectToHttps
                domain
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(values: IFormValue) {
    if (data) {
      await updateMinisiteMutation({
        variables: {
          _id: data.minisites.items[0]._id,
          status: values.status,
          redirectToHttps: values.redirectToHttps,
          domains: values.domains.filter(Boolean),
          seoKeywords: values.seoKeywords.split(',').map((item) => item.trim()),
        },
      });
    } else {
      await createMinisiteMutation({
        variables: {
          companyId: company._id,
          status: values.status,
          redirectToHttps: values.redirectToHttps,
          domains: values.domains.filter(Boolean),
          seoKeywords: values.seoKeywords.split(',').map((item) => item.trim()),
        },
      });
    }
  }

  const { values, setValue, handleSubmit, setValues } = useForm<IFormValue>({
    defaultValues: {
      status: false,
      redirectToHttps: false,
      domains: [''],
      seoKeywords: '',
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};
      const tempValueDomain = tempValues.domains.filter(Boolean);
      if (!tempValueDomain.length) {
        newErrors.domains = t('please_enter_at_least__count____type__', {
          count: 1,
          type: t('domain'),
        });
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  useEffect(() => {
    if (!loading && data && data.minisites.items.length) {
      const minisite = data.minisites.items[0];
      setValues({
        status: minisite.status,
        redirectToHttps: minisite.redirectToHttps,
        domains: minisite.domain,
        seoKeywords: minisite.seoKeywords ? minisite.seoKeywords.join(',') : '',
      });
    }
  }, [loading]);

  if (minisiteId !== 'new' && loading && minisiteId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const minisite = minisiteId && data ? data.minisites.items[0] : null;

  if (!minisite && minisiteId !== 'new') {
    return <NotFound />;
  }

  return (
    <Paper square className={classes.paper}>
      <Helmet title={`${t('detail')} | ${t('minisite')}`} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {minisite ? (
              <>
                {t('update')} #{minisite._id} {t('minisite')}
              </>
            ) : (
              <>
                {t('create')} {t('minisite')}
              </>
            )}
          </Typography>
        </Grid>
        <Grid container item xs={12} spacing={3}>
          <Grid item container spacing={2}>
            <SuperAdminPermission hideTooltip>
              <Grid item xs={12}>
                <FormGroup row>
                  <SuperAdminPermission>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.status}
                          onChange={() => setValue('status', !values.status)}
                          color="primary"
                        />
                      }
                      label={t('publish')}
                    />
                  </SuperAdminPermission>
                </FormGroup>
              </Grid>
            </SuperAdminPermission>
            <Grid container item xs={12} sm={6} md={4} lg={3} spacing={1}>
              {values.domains.map((domain, index) => (
                <Grid item xs={12}>
                  <TextField
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    fullWidth
                    placeholder="keepital.com"
                    label={`${index + 1}. ${t('domain_name')}`}
                    value={domain}
                    onChange={(e) => {
                      const value = [...values.domains];

                      value[index] = e.target.value;
                      setValue('domains', value);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              const value = [...values.domains];
                              value.splice(index, 1);
                              setValue('domains', value);
                            }}
                            size="large"
                          >
                            <RemoveIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    const value = [...values.domains, ''];
                    setValue('domains', value);
                  }}
                  startIcon={<AddIcon />}
                >
                  {`${t('add')} ${t('domain')}`}
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${t('minisite')} SEO ${t('keyword_separate_by_comma')}`}
                onChange={(e) => {
                  setValue('seoKeywords', e.target.value);
                }}
                value={values.seoKeywords}
                placeholder={t('keyword_separate_by_comma')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <p
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: t(
                    'remember_to_point_your_domain_name_A_Record_to_our_server_ip_address__ip__',
                    { ip: '35.241.11.241' }
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <StickyFormFooter
          onCancel={() =>
            history.push(
              path('minisite.list', {
                companyID: company._id,
              })
            )
          }
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingMinisite || isCreatingMinisite}
        />
      </Grid>
    </Paper>
  );
}

export default () => <MinisiteLayout main={<MinisiteDetail />} />;
